import React from "react";
import styled from "styled-components";

// One-shot error boundary so a single broken section can't blank the whole
// rebranded homepage. Shows a friendly fallback with a retry action and
// reports caught errors to Airbrake when available (the rest of the app uses
// the same global — see `app/views/layouts/inertia.html.erb`).

const Fallback = styled.div`
  margin: 24px auto;
  max-width: 520px;
  padding: 20px 24px;
  border-radius: 14px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  color: #9a3412;
  display: flex;
  gap: 14px;
  align-items: flex-start;
  font-size: 13px;

  [data-theme="dark"] & {
    background: rgba(154, 52, 18, 0.15);
    border-color: rgba(254, 215, 170, 0.2);
    color: #fed7aa;
  }
`;

const Icon = styled.span`
  font-size: 20px;
  line-height: 1;
`;

const Body = styled.div`
  flex: 1;
`;

const Title = styled.div`
  font-weight: 700;
  margin-bottom: 4px;
`;

const Retry = styled.button`
  margin-top: 10px;
  background: transparent;
  border: 1px solid currentColor;
  color: inherit;
  padding: 6px 12px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  transition: background 150ms ease;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
`;

export default class SectionErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    const tag = `rebrand:${this.props.name || "section"}`;
    if (typeof console !== "undefined") {
      console.error(`[${tag}]`, error, info);
    }
    try {
      if (typeof window !== "undefined" && window.Airbrake?.notify) {
        window.Airbrake.notify({
          error,
          context: { component: tag, componentStack: info?.componentStack },
        });
      }
    } catch (_err) {
      /* never rethrow from boundary */
    }
  }

  handleRetry = () => {
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <Fallback role="alert">
          <Icon aria-hidden="true">⚠️</Icon>
          <Body>
            <Title>Bagian ini gagal dimuat</Title>
            <div>
              Silakan muat ulang halaman. Tim kami sudah menerima laporan
              otomatisnya.
            </div>
            <Retry type="button" onClick={this.handleRetry}>
              Muat Ulang
            </Retry>
          </Body>
        </Fallback>
      );
    }
    return this.props.children;
  }
}
